/* eslint-disable react/display-name */
import React, { useState, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  LabelValue,
  WbsLabelValueSearchResult,
  SelectLabelOptionsQueryVariables
} from 'graphql/types';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { mappedLabelValues, LabelValueType } from '../LabelFacetDetails';
import { SearchBoxProps } from '../type';
import { scoredSuggestions } from '../scoredSuggestions';
import { searchOefs, searchOefsByTerm } from './hooks/useOefLabelSearch';

export const cleanSearchTerm = (value: string): string =>
  value.replace(/['",]+/g, '');

type SearchOefsHandler = (
  variables: SelectLabelOptionsQueryVariables
) => Promise<WbsLabelValueSearchResult[]>;

const matchOption = (searchOefs: SearchOefsHandler, labelId: string) => async (
  _: LabelValue[],
  term: string
): Promise<LabelValueType | null> => {
  if (!term) return null;
  const labelValues = await searchOefs({
    id: labelId,
    input: {
      query: cleanSearchTerm(term)
    }
  });

  const mappedValues = mappedLabelValues(labelValues, labelId);
  const option = mappedValues && mappedValues.length > 0 && mappedValues[0];
  return option && option.displayText ? option : null;
};

const withCriteriaProvider = (
  tag: string,
  context: React.Context<LabelCriteriaProviderContextType>,
  label: TimesheetLabel
) => (
  BaseComponent: React.FunctionComponent<SearchBoxProps>
): React.FC<SearchBoxProps> => (props: SearchBoxProps) => {
  const { searchCriteria: { criterions = {} } = {} } = props;
  const [selected, setSelected] = useState<LabelValueType[]>(
    (criterions[tag] as LabelValueType[]) || []
  );
  const apolloClient = useApolloClient();
  const $searchOefs = searchOefs({
    client: apolloClient
  });
  const searchOefSuggestion = searchOefsByTerm({
    client: apolloClient,
    labelId: label.id
  });

  const value = useMemo(
    () => ({
      selected,
      setSelected,
      options: [],
      matchOption: matchOption($searchOefs, label.id),
      searchOefs: $searchOefs,
      suggestions: scoredSuggestions(searchOefSuggestion)
    }),
    [selected, $searchOefs, searchOefSuggestion]
  );

  return (
    <context.Provider value={value}>
      <BaseComponent {...props} />
    </context.Provider>
  );
};

type LabelCriteriaProviderContextType = {
  selected: LabelValueType[];
  setSelected: React.Dispatch<React.SetStateAction<LabelValueType[]>>;
  matchOption: (
    options: LabelValue[],
    term: string
  ) => Promise<LabelValueType | null>;
  searchOefs: SearchOefsHandler;
};

const makeCriteriaProvider = (label: TimesheetLabel) => (
  tag: string,
  context: React.Context<LabelCriteriaProviderContextType>
): ((
  BaseComponent: React.FunctionComponent<SearchBoxProps>
) => React.FC<SearchBoxProps>) => withCriteriaProvider(tag, context, label);

export default makeCriteriaProvider;
